'use client';
import Image from '@repo/ui/src/components/Image/New';
import { ToutContent, ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ButtonStyleEnum, ButtonIconEnum } from '@repo/ui/src/enums/shared-enums';
import { AuthorProps, RecipeMetaSectionProps, MetaProps } from '@repo/ui/src/types';
import { CurrentUrlProps } from '@repo/ui/src/types/currentUrl';
import { useIsSM } from '@repo/ui/src/utils/useMediaQuery';

interface RecipeCardProps {
  _meta: MetaProps;
  assets?: Array<{
    endpoint: string;
    name: string;
  }>;
  author?: AuthorProps;
  recipeMetaSection?: RecipeMetaSectionProps;
  heading?: string;
  link?: string;
  linkLabel?: string;
  by?: string;
  prepTimeLabel?: string;
  cookTimeLabel?: string;
  outOf?: string;
  currentUrl?: CurrentUrlProps;
}

const RecipeCard = ({
  _meta,
  assets,
  author,
  recipeMetaSection,
  heading,
  link,
  linkLabel,
  by,
  prepTimeLabel,
  cookTimeLabel,
}: RecipeCardProps) => {
  const isSm = useIsSM();

  const buttons = [
    {
      link: { link: _meta.deliveryKey, label: linkLabel, displayName: link },
      style: ButtonStyleEnum.Text,
      icon: ButtonIconEnum.Skewer,
    },
  ];

  const imgUrl = buildImageSrc(
    assets?.length ? assets[0] : null,
    isSm ? 700 : 600,
    isSm ? 9 : 1,
    isSm ? 10 : 1,
  );

  const authorImgUrl = buildImageSrc(author?.avatar, 37, 1, 1);

  const logBackground = {
    endpoint: 'traeger',
    name: 'log-cropped-transparent',
  };

  const logBackgroundImgUrl = buildImageSrc(logBackground, 500, 0, 0);

  if (!recipeMetaSection) return null;

  return (
    <div className="flex flex-wrap flex-row justify-between sm:flex-row-reverse h-full overflow-hidden">
      <div className="relative w-full aspect-video sm:aspect-none sm:w-1/2 sm:flex-1">
        <Image
          src={imgUrl}
          alt={recipeMetaSection.title || ''}
          fill
          className="object-cover"
          priority
        />
      </div>
      <div className="w-full sm:w-1/2 relative overflow-hidden">
        <Image
          src={logBackgroundImgUrl}
          role="presentation"
          alt=""
          width={500}
          height={200}
          className="absolute bottom-0 left-[5%] w-[40%] z-[-1] sm:w-[90%]"
          aria-hidden="true"
        />

        <div className="p-4 md:p-gutterLG">
          {heading && (
            <ToutContent
              align="left"
              heading={recipeMetaSection.title}
              headingStyleVariant="h2"
              lilHeading={heading}
              className="pb-6"
            />
          )}
          {author && (
            <div className="flex items-center mb-3">
              <div className="mr-2 relative w-[30px] h-[30px] sm:w-[37px] sm:h-[37px]">
                <Image
                  src={authorImgUrl}
                  alt={author.name || ''}
                  fill
                  className="rounded-full object-cover"
                />
              </div>
              <div className="tra-text-web-body-xl">
                {by} {author.name}
              </div>
            </div>
          )}
          <ToutContent
            align="left"
            lineClamp={4}
            description={recipeMetaSection.description}
            className="pb-6"
          />
          <div className="mt-1 flex items-center justify-evenly bg-neutral-700 text-neutral-100 mx-[-1rem] p-4 md:mx-[-7.5vw] md:px-[7.5vw]">
            <div className="flex items-center">
              <span className="tra-text-web-heading-4 pr-2">{recipeMetaSection.prepTime}</span>
              <span className="tra-text-web-utl-label-s">{prepTimeLabel}</span>
            </div>
            <div className="w-px h-10 bg-warm-200 mx-[0.2rem] lg:mx-6"></div>
            <div className="flex items-center">
              <span className="tra-text-web-heading-4 pr-2">{recipeMetaSection.cookTime}</span>
              <span className="tra-text-web-utl-label-s">{cookTimeLabel}</span>
            </div>
          </div>
          <ToutContentButtons className="mt-1" buttons={buttons} align="left" />
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;
